import { AccountPermissions, ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import store from '../store';
import { BROADCAST_CHANNEL } from '@/broadcastChannel/broadcast-channel.const';
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { useRedirectToLogin } from '@/composables/redirect';
import RouterService from '@/router/router-service';
import {
	PLACEMENT_MODE_NAME,
	CREATE_AND_MANAGE_ROUTE_NAME,
	WORKSPACE_SELECTION_ROUTE_NAME,
	ACCOUNT_CHANGE_REQUIRED_ROUTE_NAME,
	BETA_PLAN_ACCOUNT_ACCESS_DENIED,
} from '@/router/router.const.js';
import ExtensionCommunicationService from '@/utils/extension-communication-service';
import { GET_TOKEN_FROM_EXT_KEY } from '@/utils/extension-communication-service.const';
import IframeService from '@/utils/iframe-service';
import { VIRTUAL_PROJECT_IDS } from '@/utils/projects.const';

const MOBILE_ALLOWED_ROUTE_NAMES = ['MobileNotSupported', WORKSPACE_SELECTION_ROUTE_NAME];
const FORBIDDEN_ROUTE = { name: 'Forbidden' };
const MOBILE_NOT_SUPPORTED_ROUTE = { name: 'MobileNotSupported' };
const HOME_ROUTE = { name: 'HomePage' };

async function isAllowedByProject(projectId, permission) {
	if (projectId && !VIRTUAL_PROJECT_IDS.includes(projectId)) {
		// if workflow does not exist we init it in store = forceGet: false (if exists we do not go and get it)
		await store.dispatch('projects/setProjectPermissions', { projectId, forceGet: false }, { root: true });
		return store.getters['projects/projectHasPermission'](projectId, permission);
	}
	return false;
}

async function shouldRedirectToBetaAccountAccessDenied() {
	// block beta users from accessing the app
	if (store.getters['account/account'].has_beta_plans) {
		await store.dispatch('billing/getEntitlements', {}, { root: true });
		const unblockEntitlement = store.getters['billing/useEntitlement'](FeatureTypes.UnblockBetaPlans);
		return !unblockEntitlement.has_access;
	}
	return false;
}

const { redirectToLogin } = useRedirectToLogin();
async function guardLoggedIn(to, from, next) {
	// notice: else is important here, next does not finish the scope
	const requiresAuthRoute = to.matched.find((route) => route.meta.requiresAuth);
	if (requiresAuthRoute) {
		if (
			IframeService.inIframe() &&
			RouterService.isLoadedFromExtension(to) &&
			(await ExtensionCommunicationService.doesSupportsRouterGuardTokenLogin())
		) {
			try {
				const credentialsFromExtensionBg = await IframeService.sendAsyncMessageToParent({ action: GET_TOKEN_FROM_EXT_KEY });
				await store.dispatch('initCredentials', { creds: credentialsFromExtensionBg });
			} catch {
				console.error('Failed to get credentials from extension background');
			}
		}
		if (!store.getters.isLoggedIn) {
			requiresAuthRoute.meta.redirectToLogin
				? redirectToLogin(store.getters['main/appURL'])
				: next({
						name: 'Login',
						query: { return_to: window.location.href, ...to.query },
				  });
		} else {
			if (store.getters.user === null) {
				await store.dispatch('initUser');
			}
			const activeAccountId = store.getters['account/activeAccountId'] || store.getters.user?.profile?.account?.id;
			if (!activeAccountId) {
				store.dispatch('account/setUserAccounts', { accounts: store.getters.user?.profile?.user_accounts }, { root: true });
				return next({ ...to, name: WORKSPACE_SELECTION_ROUTE_NAME, query: { return_to: window.location.href, ...to.query } });
			}
			const plyAccountFromQueryParam = to.query.pa;
			delete to.query.pa; // remove query param from url, so it won't get in infinite loop
			if (plyAccountFromQueryParam && String(plyAccountFromQueryParam) !== String(activeAccountId)) {
				return next({
					name: ACCOUNT_CHANGE_REQUIRED_ROUTE_NAME,
					params: { accountId: plyAccountFromQueryParam, redirect: () => next(to) },
				});
			}

			const fromBroadcastChannel = to.query[BROADCAST_CHANNEL];
			if (fromBroadcastChannel) {
				AppDialogManagerService.openWorkspaceChangedDialog({ account: store.getters['account/account'] });
				const query = { ...to.query };
				delete query[BROADCAST_CHANNEL]; // remove query param from url, so it won't get in infinite loop
				return next({ ...to, query });
			}

			if (to.name != BETA_PLAN_ACCOUNT_ACCESS_DENIED && (await shouldRedirectToBetaAccountAccessDenied())) {
				return next({ name: BETA_PLAN_ACCOUNT_ACCESS_DENIED });
			}

			next();
		}
	} else {
		// no auth required
		next();
	}
}

function guardMobile(to, from, next) {
	// ALL WHITE-LISTED MOBILE ROUTE NAMES SHOULD BE ADDED TO MOBILE_ALLOWED_ROUTE_NAMES ARRAY
	if (to.matched.some((route) => route.meta.requiresAuth)) {
		const isAllowedFromMobile = MOBILE_ALLOWED_ROUTE_NAMES.includes(to.name);
		if (isAllowedFromMobile) {
			next();
		} else {
			next(MOBILE_NOT_SUPPORTED_ROUTE);
		}
	} else {
		// no auth required
		next();
	}
}

async function guardCreateWorkflowInProject(to, from, next) {
	const projectId = parseInt(to.params.pID);

	const allowed = await isAllowedByProject(projectId, ProjectPermissions.ChangeWorkflows);

	if (allowed) {
		next();
	} else {
		next(FORBIDDEN_ROUTE);
	}
}

async function guardEditWorkflowPermissions(to, from, next) {
	const workflowId = parseInt(to.params.wfID);
	const paramProjectId = parseInt(to.params.pID);

	// true because this is probably part of the route and not full route
	let allowedByWorkflow = false;
	let allowedByProject = false;
	let projectId;
	if (workflowId) {
		// if workflow does not exist we init it in store = forceGet: false (if exists we do not go and get it)
		try {
			const wf = await store.dispatch(
				'workflows/getWorkflowData',
				{ id: workflowId, forceGet: false, forceGetNodes: false },
				{ root: true }
			);
			projectId = wf?.project;
		} catch {
			next({ ...HOME_ROUTE, replace: true });
			return;
		}

		allowedByWorkflow = store.getters['workflows/hasWorkflowEditPermissions'](workflowId);
		allowedByProject = paramProjectId === projectId && (await isAllowedByProject(projectId, ProjectPermissions.ViewProject));
	}

	if (allowedByProject || allowedByWorkflow) {
		if (projectId !== paramProjectId) {
			const nextObj = {
				params: { ...to.params, pID: projectId },
				name: to.name,
				hash: to.hash,
				query: to.query,
				replace: true,
			};
			next(nextObj);
		} else {
			next();
		}
	} else {
		next(FORBIDDEN_ROUTE);
	}
}

async function guardIfCanManageGroups(to, from, next) {
	if (!store.getters['account/members']) {
		await store.dispatch('account/getAccountMembers');
	}
	if (store.getters['account/hasAccountPermission'](AccountPermissions.ManageGroups)) {
		next();
	} else {
		next(FORBIDDEN_ROUTE);
	}
}

async function guardCreateAndManage(to, from, next) {
	if (to.name !== CREATE_AND_MANAGE_ROUTE_NAME) {
		next();
		return;
	}
	let isPlacementModeEnabled = store.getters['featureFlags/isPlacementModeEnabled'];
	const featureFlagDidNotInitialize = isPlacementModeEnabled === undefined; // This happens on refresh
	if (featureFlagDidNotInitialize) {
		// wait for feature flag to initialize, and set value again
		await new Promise((resolve) => setTimeout(resolve, 1000));
		isPlacementModeEnabled = store.getters['featureFlags/isPlacementModeEnabled'];
	}
	if (isPlacementModeEnabled) {
		next({ name: PLACEMENT_MODE_NAME });
	} else {
		next({ name: 'ProjectsRoot' });
	}
}

export default {
	guardLoggedIn,
	guardCreateWorkflowInProject,
	guardEditWorkflowPermissions,
	guardIfCanManageGroups,
	guardMobile,
	guardCreateAndManage,
};
